import React, { useState, useEffect } from 'react';
import api from '../../api/api'; // Adjust path based on your folder structure
import { 
    X, 
    Send, 
    Loader2, 
    Check, 
    User, 
    Mail, 
    Phone, 
    MessageSquare, 
    MapPin, 
    Briefcase 
} from 'lucide-react';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import styles from './EnquiryModal.module.css';

const EnquiryModal = ({ business, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', message: ''
    });

    // Prefill data if user is logged in
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setFormData(prev => ({
                    ...prev,
                    //name: decoded.unique_name || decoded.given_name || '', 
                    email: decoded.email || ''
                }));
            } catch (e) { console.error(e); }
        }
    }, []);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const isUnclaimed = !business.isVerified; 

            if (isUnclaimed) {
                // Scenario 1: Unclaimed Business
                const payload = {
                    CrmId: business.originalCrmId || business.businessId, 
                    senderName: formData.name,
                    senderEmail: formData.email,
                    senderPhone: formData.phone,
                    message: formData.message
                };
                await api.post('/api/Leads/send-unclaimed', payload);
            } else {
                // Scenario 2: Verified Business
                if (!token) {
                    toast.error("Please login to message verified businesses.");
                    setLoading(false);
                    return;
                }
                
                const decodedToken = jwtDecode(token);
                const userType = decodedToken.UserType;
                const homeUserId = parseInt(localStorage.getItem('loggedInHomeUserId') || 0);
                const bizUserId = parseInt(localStorage.getItem('loggedInBusinessId') || 0);

                const payload = {
                    BusinessId: business.businessId,
                    senderName: formData.name,
                    senderEmail: formData.email,
                    senderPhone: formData.phone,
                    senderUserId: userType === 'Home' ? homeUserId : bizUserId,
                    senderUserType: userType === 'Home' ? 'HomeUser' : 'BusinessUser',
                    message: formData.message
                };
                await api.post('/api/Leads/send-enquiry', payload);
            }

            setSuccess(true);
        } catch (error) {
            console.error(error);
            toast.error('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // --- RENDER: Success State ---
    if (success) {
        return (
            <div className={styles.overlay} onClick={onClose}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.successView}>
                        <div className={styles.successIconWrapper}>
                            <Check size={48} color="#22c55e" strokeWidth={3} />
                        </div>
                        <h3 className={styles.successTitle}>Enquiry Sent!</h3>
                        <p className={styles.successMessage}>
                            We have sent your message to <strong>{business.businessName}</strong>. 
                            <br />They typically respond within 24 hours.
                        </p>
                        <button onClick={onClose} className={styles.successCloseBtn}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- RENDER: Form State ---
    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                
                {/* 1. Header */}
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <div style={{ color: '#F5A623' }}>
                            <MessageSquare size={24} />
                        </div>
                        <div>
                            <h2>Send Message</h2>
                            <p>Contact this professional directly</p>
                        </div>
                    </div>
                    <button onClick={onClose} className={styles.closeBtn}>
                        <X size={24} />
                    </button>
                </div>
                
                {/* 2. Scrollable Content */}
                <div className={styles.content}>
                    
                    {/* Business Summary Card */}
                    <div className={styles.businessSummary}>
                        <div className={styles.summaryIcon}>
                            <Briefcase size={20} />
                        </div>
                        <div className={styles.summaryDetails}>
                            <h4>{business.businessName}</h4>
                            <div className={styles.summaryMeta}>
                                {business.suburb && (
                                    <>
                                        <MapPin size={14} /> 
                                        {business.suburb} {business.postcode}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <form id="enquiryForm" onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Your Name</label>
                            <div className={styles.inputWrapper}>
                                <User size={18} className={styles.inputIcon} />
                                <input 
                                    className={styles.input}
                                    name="name" 
                                    value={formData.name} 
                                    onChange={handleChange} 
                                    required 
                                    placeholder="Your name" 
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>Email Address</label>
                            <div className={styles.inputWrapper}>
                                <Mail size={18} className={styles.inputIcon} />
                                <input 
                                    type="email" 
                                    className={styles.input}
                                    name="email" 
                                    value={formData.email} 
                                    onChange={handleChange} 
                                    required 
                                    placeholder="name@example.com" 
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>Phone Number (Optional)</label>
                            <div className={styles.inputWrapper}>
                                <Phone size={18} className={styles.inputIcon} />
                                <input 
                                    type="tel" 
                                    className={styles.input}
                                    name="phone" 
                                    value={formData.phone} 
                                    onChange={handleChange} 
                                    placeholder="0400 000 000" 
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>How can they help?</label>
                            <textarea 
                                className={styles.textarea}
                                name="message" 
                                value={formData.message} 
                                onChange={handleChange} 
                                required 
                                rows={4} 
                                placeholder="Hi, I would like a quote for..." 
                            />
                        </div>
                    </form>
                </div>

                {/* 3. Footer */}
                <div className={styles.footer}>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className={styles.cancelButton}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        form="enquiryForm" 
                        disabled={loading} 
                        className={styles.submitButton}
                    >
                        {loading ? (
                            <>
                                <Loader2 className={styles.spin} size={18} /> Sending...
                            </>
                        ) : (
                            <>
                                <Send size={18} /> Send Message
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EnquiryModal;